import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../auth/stores/AuthContext';
import { STORAGE_KEYS } from '../../config/storageKeys';
import './PathbuddyMovedBanner.css';

/**
 * Pathbuddy is retired. pathbuddy.com and pathbuddy.app send visitors here
 * with PATHBUDDY_MARKER in the query (public/index.html, hostRedirects.js).
 * This banner tells them the product is the same under a new name, then
 * takes the marker out of the address bar so it is not bookmarked or shared.
 */
export const PATHBUDDY_MARKER = 'from=pathbuddy';

/** Does this query string carry the marker? */
export function hasPathbuddyMarker(search) {
  const raw = String(search || '').replace(/^\?/, '');
  if (!raw) return false;
  return raw.split('&').indexOf(PATHBUDDY_MARKER) !== -1;
}

/**
 * The query string without the marker. Every other parameter is kept byte for
 * byte (a reset token or join code must arrive exactly as it was sent).
 */
export function withoutPathbuddyMarker(search) {
  const raw = String(search || '').replace(/^\?/, '');
  const kept = raw.split('&').filter((part) => part && part !== PATHBUDDY_MARKER);
  return kept.length ? `?${kept.join('&')}` : '';
}

function wasDismissed() {
  try {
    return window.localStorage.getItem(STORAGE_KEYS.PATHBUDDY_MOVED_DISMISSED) === '1';
  } catch (err) {
    return false;
  }
}

function rememberDismissed() {
  try {
    window.localStorage.setItem(STORAGE_KEYS.PATHBUDDY_MOVED_DISMISSED, '1');
  } catch (err) {
    // Storage blocked (private mode): the banner still closes for this page.
  }
}

export default function PathbuddyMovedBanner() {
  const auth = useContext(AuthContext);
  // Read once, without side effects, so a double render still sees it.
  const [open, setOpen] = useState(
    () => typeof window !== 'undefined' && hasPathbuddyMarker(window.location.search) && !wasDismissed()
  );

  useEffect(() => {
    const { pathname, search, hash } = window.location;
    if (!hasPathbuddyMarker(search)) return;
    window.history.replaceState(window.history.state, '', `${pathname}${withoutPathbuddyMarker(search)}${hash}`);
  }, []);

  if (!open) return null;

  const close = () => {
    rememberDismissed();
    setOpen(false);
  };
  const signedIn = Boolean(auth && auth.isAuthenticated);

  return (
    <section className="ivori-pathbuddy-moved" aria-label="Pathbuddy is now IVORI">
      <div className="ivori-pathbuddy-moved__body">
        <p className="ivori-pathbuddy-moved__title">Pathbuddy is now IVORI</p>
        <p className="ivori-pathbuddy-moved__text">
          Same platform, new name.
          {!signedIn && (
            <>
              {' '}Had a Pathbuddy account? <Link to="/login" onClick={close}>Sign in</Link> with the same details.
            </>
          )}
        </p>
      </div>
      <button
        type="button"
        className="ivori-pathbuddy-moved__close"
        onClick={close}
        aria-label="Dismiss"
      >
        <span aria-hidden="true">×</span>
      </button>
    </section>
  );
}
